import { useRef, useState, type ChangeEvent } from 'react';
import { CatalogList } from './CatalogList';
import { MarketplaceImport } from './MarketplaceImport';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { ApiError } from '@/api/client';
import { LoadingState, EmptyState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatNumber } from '@/i18n/format';
import type { CatalogImportPreview, SkuImportCommitResult, SkuImportOnExisting, SkuImportPreview } from '@/api/types';
import { XLSX_MIME, downloadBase64, fileToBase64 } from '@/lib/requests';
import {
  RESTART_CODES, STATUS_KEYS, STATUS_TONE, describeSkuImportError, describeSkuRow, precheckSkuFile, sortRowsErrorsFirst,
} from '@/lib/skuImport';

/**
 * The seller's product catalogue (Products): the permanent list of every SKU / variant, with
 * «Download template» and «Upload Excel» always on the page. Importing is part of the same page:
 *   template -> fill in -> upload -> per-row check -> preview -> confirm -> the goods are in the list below.
 * The upload only CHECKS the file (nothing is written to the catalogue); the
 * single primary action - confirm - imports exactly what was checked, by batch
 * id. Any row error refuses the whole file, so the user fixes it and uploads
 * again. With one company the chooser is hidden and no owner id is sent.
 */
export function ProductsPage(): JSX.Element {
  const { t, locale } = useLanguage();
  const options = useApi(() => endpoints.skuImportOptions(), []);
  const [ownerId, setOwnerId] = useState('');
  const [onExisting, setOnExisting] = useState<SkuImportOnExisting>('SKIP');
  const [preview, setPreview] = useState<SkuImportPreview | CatalogImportPreview | null>(null);
  // Where the check result came from: an Excel file or a connected marketplace (same check, same confirm).
  const [source, setSource] = useState<'file' | 'marketplace'>('file');
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);
  const [result, setResult] = useState<SkuImportCommitResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [checking, setChecking] = useState(false);
  const [committing, setCommitting] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  // The batch the seller has just confirmed: the list below highlights its goods; refreshKey re-reads the list.
  const [highlightBatch, setHighlightBatch] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const owners = options.status === 'success' ? options.data.owners : [];
  const single = owners.length === 1;
  const chosenOwner = single ? owners[0]!.id : ownerId;
  const needsOwner = owners.length > 1 && !ownerId;

  function startOver(): void {
    setPreview(null);
    setResult(null);
    setError(null);
  }

  async function onTemplate(): Promise<void> {
    setError(null);
    try {
      const r = await endpoints.skuImportTemplate(locale);
      downloadBase64(r.filename, r.bytesBase64, XLSX_MIME);
    } catch (e) {
      setError(e);
    }
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    setError(null);
    setResult(null);
    setPreview(null);
    setSource('file');
    const refused = precheckSkuFile(file);
    if (refused) {
      setError(refused);
      input.value = '';
      return;
    }
    setChecking(true);
    try {
      const fileBase64 = await fileToBase64(file);
      setPreview(
        await endpoints.skuImportPreview({
          ...(single || !ownerId ? {} : { ownerId }),
          fileName: file.name,
          onExisting,
          fileBase64,
        }),
      );
    } catch (err) {
      setError(err);
    } finally {
      setChecking(false);
      input.value = '';
    }
  }

  async function onConfirm(): Promise<void> {
    if (!preview) return;
    setCommitting(true);
    setError(null);
    try {
      setResult(await endpoints.skuImportCommit(preview.batchId));
      setHighlightBatch(preview.batchId);
      setRefreshKey((k) => k + 1);
      setPreview(null);
    } catch (err) {
      setError(err);
      // The check no longer matches the catalogue (or the file itself was refused): upload again.
      if (err instanceof ApiError && RESTART_CODES.has(err.code)) setPreview(null);
    } finally {
      setCommitting(false);
    }
  }

  if (options.status === 'loading') return <LoadingState />;
  if (options.status === 'error') return <ErrorBanner error={options.error} onRetry={options.reload} />;
  if (owners.length === 0) {
    return (
      <div>
        <div className="page-header"><h1>{t('skuImport.title')}</h1></div>
        <EmptyState title={t('skuImport.noCompanyTitle')} hint={t('skuImport.noCompanyHint')} />
      </div>
    );
  }

  const toImport = preview ? preview.totals.new + preview.totals.update : 0;
  const hasErrors = preview ? preview.totals.error > 0 : false;
  // An Excel file with a bad row is refused whole (the seller fixes the file); a marketplace read imports the valid cards and reports the rest.
  const blocked = hasErrors && source === 'file';
  const market = preview !== null && 'found' in preview ? preview : null;

  return (
    <div className="request-form">
      <div className="page-header">
        <div>
          <h1>{t('skuImport.title')}</h1>
          <div className="sub">{t('skuImport.subtitle')}</div>
        </div>
        <div className="form-actions" style={{ margin: 0 }}>
          <button type="button" onClick={() => void onTemplate()}>{t('skuImport.downloadTemplate')}</button>
          <button type="button" aria-expanded={marketplaceOpen} disabled={checking || needsOwner} onClick={() => setMarketplaceOpen((v) => !v)}>
            {t('mpImport.open')}
          </button>
          <button type="button" className="primary" disabled={checking || needsOwner || !chosenOwner} onClick={() => fileInput.current?.click()}>
            {checking ? t('skuImport.checking') : t('skuImport.upload')}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            aria-label={t('skuImport.upload')}
            onChange={(e) => void onFile(e)}
          />
        </div>
      </div>

      {error !== null && (
        <div className="error-banner" role="alert">{describeSkuImportError(t, error, source)}</div>
      )}

      {marketplaceOpen && (
        <MarketplaceImport
          onExisting={onExisting}
          disabled={committing}
          confirmedBatchId={highlightBatch}
          onPreview={(p) => { setSource('marketplace'); setResult(null); setPreview(p); }}
          onError={(e) => { setSource('marketplace'); setError(e); setPreview(null); }}
          onClose={() => setMarketplaceOpen(false)}
        />
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <p className="sub" style={{ marginTop: 0 }}>{needsOwner ? t('skuImport.companyRequired') : t('skuImport.templateHint')}</p>
        {owners.length > 1 && (
          <div className="field-row">
            <label htmlFor="sku-owner">{t('skuImport.company')}</label>
            <select id="sku-owner" value={ownerId} onChange={(e) => { setOwnerId(e.target.value); setPreview(null); }}>
              <option value="">{t('skuImport.chooseCompany')}</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="sub" style={{ padding: 0, marginBottom: 4 }}>{t('skuImport.onExisting')}</legend>
          {(['SKIP', 'UPDATE'] as const).map((v) => (
            <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 44 }}>
              <input
                type="radio"
                name="sku-on-existing"
                value={v}
                checked={onExisting === v}
                style={{ minHeight: 20, width: 20, height: 20, flex: '0 0 auto' }}
                onChange={() => { setOnExisting(v); setPreview(null); }}
              />
              {t(v === 'SKIP' ? 'skuImport.onExisting.skip' : 'skuImport.onExisting.update')}
            </label>
          ))}
        </fieldset>
      </div>

      {result && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="notice" role="status">
            {source === 'marketplace' ? (
              <>
                {[
                  result.replayed ? t('skuImport.doneReplayed') : '',
                  t('mpImport.done', { created: formatNumber(result.created, locale), updated: formatNumber(result.updated, locale), skipped: formatNumber(result.skipped, locale) }),
                  t('catalog.afterImport'),
                ].filter(Boolean).join(' ')}{' '}
                <a href="#catalog-list" onClick={() => document.getElementById('catalog-list')?.scrollIntoView?.({ behavior: 'smooth' })}>{t('mpImport.toCatalog')}</a>
              </>
            ) : (
              [
                result.replayed ? t('skuImport.doneReplayed') : '',
                t('skuImport.done', { created: formatNumber(result.created, locale), updated: formatNumber(result.updated, locale) }),
                result.skipped > 0 ? t('skuImport.doneSkipped', { skipped: formatNumber(result.skipped, locale) }) : '',
                t('catalog.afterImport'),
              ].filter(Boolean).join(' ')
            )}
          </div>
        </div>
      )}

      {preview && !result && (
        <div className="card" style={{ marginBottom: 16 }} aria-label={t('skuImport.previewTitle')}>
          <h2 className="section-title">{t('skuImport.previewTitle')}</h2>
          {preview.alreadyImported ? (
            <div className="notice" role="status">{t(source === 'marketplace' ? 'mpImport.alreadyImported' : 'skuImport.alreadyImported')}</div>
          ) : (
            <>
              <div className={blocked ? 'error-banner' : 'notice'} role={blocked ? 'alert' : 'status'}>
                {t(market ? 'mpImport.summary' : 'skuImport.summary', {
                  new: formatNumber(preview.totals.new, locale),
                  update: formatNumber(preview.totals.update, locale),
                  skip: formatNumber(preview.totals.skip, locale),
                  error: formatNumber(preview.totals.error, locale),
                  cards: formatNumber(market?.found.cards ?? 0, locale),
                  variants: formatNumber(market?.found.variants ?? 0, locale),
                })}
              </div>
              {market && market.renamed > 0 && (
                <p className="sub" role="note">{t('mpImport.renamed', { n: formatNumber(market.renamed, locale) })}</p>
              )}
              {market && market.notFound !== null && market.notFound > 0 && (
                <p className="sub" role="note">{t('mpImport.notFound', { n: formatNumber(market.notFound, locale) })}</p>
              )}
              <p className="sub">{hasErrors ? t(source === 'marketplace' ? 'mpImport.hasErrors' : 'skuImport.hasErrors') : t('skuImport.nothingSaved')}</p>
              {'nextCursor' in preview && preview.nextCursor && <div className="notice" role="status">{t('mpImport.more')}</div>}
              <div className="table-scroll">
                <table aria-label={t('skuImport.previewTitle')}>
                  <thead>
                    <tr>
                      <th>{t('skuImport.col.row')}</th>
                      <th>{t('skuImport.col.code')}</th>
                      <th>{t('skuImport.col.name')}</th>
                      <th>{t('skuImport.col.size')}</th>
                      <th>{t('skuImport.col.color')}</th>
                      <th>{t('skuImport.col.barcode')}</th>
                      <th>{t('skuImport.col.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortRowsErrorsFirst(preview.rows).map((r) => {
                      const message = describeSkuRow(t, r, source);
                      return (
                        <tr key={r.rowNumber}>
                          <td>{r.rowNumber}</td>
                          <td>{r.article && r.article !== r.code ? `${r.article} → ${r.code}` : r.code}</td>
                          <td>{r.name}</td>
                          <td>{r.size}</td>
                          <td>{r.color}</td>
                          <td>{r.barcode}</td>
                          <td>
                            <span className={`badge ${STATUS_TONE[r.status]}`}>{t(STATUS_KEYS[r.status])}</span>
                            {message && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{message}</div>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="form-actions">
            {!preview.alreadyImported && (
              <button
                type="button"
                className="primary"
                disabled={committing || blocked || !preview.canCommit}
                onClick={() => void onConfirm()}
              >
                {committing ? t('skuImport.importing') : t('skuImport.import', { n: formatNumber(toImport, locale) })}
              </button>
            )}
            <button type="button" disabled={committing} onClick={startOver}>{t(source === 'marketplace' ? 'mpImport.discard' : 'skuImport.uploadAnother')}</button>
          </div>
        </div>
      )}

      <div id="catalog-list">
        <CatalogList highlightBatch={highlightBatch} refreshKey={refreshKey} />
      </div>
    </div>
  );
}
